import { useEffect, useCallback, useState, useRef, type default as React } from 'react';

import { Box } from '@mui/material';
import { useAuthContext } from 'AuthProvider';
import { MasterSearchFrom, FileDownloadData } from 'components/commonparts'
import { ItemAlertDialog } from 'components/dialog';
import { SearchCondition, DataList, SearchBox } from 'components/parts/MasterItem';
import { useCenter } from 'hooks/useCenter';
import { useGridSearch } from 'hooks/useGridSearch';
import { useItem } from 'hooks/useItems';
import { useOrderAlert } from 'hooks/useOrderAlert';
import { useSupplier } from 'hooks/useSupplier';
import { useTypecode } from 'hooks/useTypecode';
import { useSearchParamStore } from 'store/searchParamStore';
import { getLocalSearchParam } from 'utils/getLocalSearchParam';

import type { GetMstItemsItemGetRequest, MstItem, ResponseResult, SearchTargetEnabled } from 'api-client';
import type { ItemAlertDialogEdit } from 'components/dialog/ItemAlertDialog/ItemAlertDialog';

function MasterItem() {
  const { loginUserInfo, typeCode: typeCodes } = useAuthContext()
  const trCompanyId = loginUserInfo?.companyId;
  const setSearchParamState = useSearchParamStore((state) => state.setSearchParamState)
  const searchParamState = useSearchParamStore((state) => state.searchParamState)

  const localSearchParam = getLocalSearchParam(searchParamState, loginUserInfo?.tenantId, loginUserInfo?.companyId)
  const localOrgSupplierId = localSearchParam?.item.orgSupplierId || ''
  const localSearchTargetEnabled = localSearchParam?.item.searchTargetEnabled
  const defaultSearchTargetEnabled: SearchTargetEnabled | undefined =
  typeCodes.find((group) => group.typeGroup === 'search_target_enabled')?.codes.find((code) => code.defaultFlg)?.typeCode as SearchTargetEnabled | undefined 

  // 自社のcompanyIdのみ検索条件をローカルストレージから復元
  const initSearchParam: GetMstItemsItemGetRequest = {
    companyId: trCompanyId ?? loginUserInfo!.companyId,
    orgSupplierId: localOrgSupplierId,
    searchTargetEnabled: localSearchTargetEnabled ?? defaultSearchTargetEnabled,
  }

  const [hasGridEdited, setHasGridEdited] = useState<boolean>(false)
  const [downloadGridRows, setDownloadGridRows] = useState<MstItem[]>([])
  const [downloadMode, setDownloadMode] = useState<string>('csv')
  const [downloadColDef, setDownloadColDef] = useState<any[] | undefined>([]);

  const [resultMessage, setResultMessage] = useState<ResponseResult | undefined>(undefined)
  const [gridMessage, setGridMessage] = useState<ResponseResult | undefined>(undefined)

  const { result, items, fetchItems, isLoading, } = useItem()
  const { fetchCenterSearch, centerSearch } = useCenter()
  const { fetchAlertSummary } = useOrderAlert()
  const { supplierSearch, fetchSupplierSearch } = useSupplier()
  const { typeCode, fetchTypeCode } = useTypecode()
  const [searchParam, setSearchParam] = useState<GetMstItemsItemGetRequest>(initSearchParam)
  const isFirstRender = useRef(true);
  const [updateTrigger, setUpdateTrigger] = useState<boolean | undefined>(undefined);
  const [uploadData, setUploadData] = useState<any[]>([])
  const [searchTrigger, setSearchTrigger] = useState<boolean>(false);
  const [discardChangeDialogOpen, setDiscardChangeDialogOpen] = useState<boolean>(false)
  const [alignmentFlag, setAlignmentFlag] = useState<boolean>(false)
  const [isAlert, setIsAlert] = useState<boolean | null>(null)
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState<boolean>(false)
  const [alertDialogResetSignal, setAlertDialogResetSignal] = useState(0)
  const [alertDialogEdits, setAlertDialogEdits] = useState<ItemAlertDialogEdit[]>([])
  const [alertDialogFocusTarget, setAlertDialogFocusTarget] = useState<{ orgItemCode: string } | undefined>(undefined)
  
  // Search functionality using custom hook (hidden by default, shown when Ctrl+F)
  const search = useGridSearch({ autoShowOnCtrlF: true })

  useEffect(() => {
    setResultMessage(result)
  }, [result])

  useEffect(() => {
    setResultMessage(gridMessage)
  }, [gridMessage])

  const doSearch = useCallback(async (requestParam: GetMstItemsItemGetRequest) => {
    setAlertDialogEdits([])
    setAlertDialogResetSignal((prev) => prev + 1)

    try {
        const [, alertSummaryResponse] = await Promise.all([
          fetchItems({
            enabled: false,
            ...requestParam,
            pageNo: 0,
          }),
          fetchAlertSummary({
            enabled: false,
            orgSupplierId: requestParam.orgSupplierId || undefined,
          }, true),
        ])
        setIsAlert(alertSummaryResponse?.data?.isMstItemAlert ?? null)
        if (requestParam.companyId === loginUserInfo!.companyId && loginUserInfo?.tenantId && loginUserInfo?.companyId) {
          setSearchParamState<GetMstItemsItemGetRequest>(
            loginUserInfo?.tenantId,
            loginUserInfo?.companyId,
            'Item',
            {
              ...requestParam,
            }
          );
        }
        setSearchParam(requestParam);
        setHasGridEdited(false)

      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching data:", error);
      } 
  }, [fetchItems, fetchAlertSummary, loginUserInfo, setSearchParamState]);

  const handleRegister = () => {
    setAlertDialogEdits([])
    setAlertDialogResetSignal((prev) => prev + 1)
    setUpdateTrigger((prev) => !prev);
  }

  const handleUploadData = async (headers: any[], data: any[]) => {
    // エクセルファイルからデータをインポートし、ヘッダーとデータ行に分けて渡される
    const mappedData = data.map((row) => {
      const obj: any = {};
      headers.forEach((header, index) => {
        obj[header] = row[index];
      });
      return obj;
    });
    setUploadData(mappedData); 
  };

  const onReload = () => {
    // 検索処理の実行を行うトリガー
    setAlertDialogEdits([])
    setAlertDialogResetSignal((prev) => prev + 1)
    setSearchTrigger((prev) => !prev);
  }

  useEffect(() => {
    const firstTimeRender = async () => { 
      if (isFirstRender.current) {
        isFirstRender.current = false
        const itemsPromise = fetchItems({
          enabled: false,
          ...searchParam,
        });
        const alertSummaryPromise = fetchAlertSummary({
          enabled: false,
          orgSupplierId: searchParam.orgSupplierId || undefined,
        }, true);
        const supplierPromise = fetchSupplierSearch({
          enabled: false,
          companyId: searchParam.companyId, 
        }, true);
        const centerPromise = fetchCenterSearch({
          enabled: false,
          companyId: searchParam.companyId,
        }, true);
        const typeCodePromise = fetchTypeCode();
        const [, alertSummaryResponse] = await Promise.all([
          itemsPromise,
          alertSummaryPromise,
          supplierPromise,
          centerPromise,
          typeCodePromise,
        ]);
        setIsAlert(alertSummaryResponse?.data?.isMstItemAlert ?? null)
      }
    }
    firstTimeRender()
    setAlignmentFlag(true)
  }, [])

  const generateDownloadData = (): MstItem[] => {
    if (!items?.list || items.list.length === 0) {
      return [{} as MstItem]
    }
    const downloadData: MstItem[] = [...items.list]
    return downloadData
  }

  const handleFileDownload = () => {
    setDownloadGridRows(generateDownloadData())
  }


  return (
    <Box>
      <MasterSearchFrom
        result={resultMessage}
        onDoUpdate={handleRegister}
        onReload={onReload}
        onImportData={handleUploadData}
        onFileDownLoad={handleFileDownload}
        discardChangeDialogOpen={discardChangeDialogOpen}
        setDownloadMode={setDownloadMode}
        hasDataEdited={hasGridEdited}
        importRequiredFields={["org_item_code"]}
        headerJp={["社内商品コード"]}
        lastUpdateDatetime={items?.lastUpdateDatetime}
      >
        <SearchCondition
            hasDataEdited={hasGridEdited}
            searchParam={initSearchParam}
            onSearch={doSearch}
            setDiscardChangeDialogOpen={setDiscardChangeDialogOpen}
            searchTrigger={searchTrigger}
            alignmentFlag={alignmentFlag}
            isAlert={isAlert}
            onAlertClick={() => setIsAlertDialogOpen(true)}
        />
      </MasterSearchFrom>
      <ItemAlertDialog
        open={isAlertDialogOpen}
        onClose={() => setIsAlertDialogOpen(false)}
        onApplyEdits={(edits: ItemAlertDialogEdit[]) => setAlertDialogEdits(edits)}
        onNavigateToRecord={(target: { orgItemCode: string }) => {
          setAlertDialogFocusTarget(target)
          setIsAlertDialogOpen(false)
        }}
        resetSignal={alertDialogResetSignal}
        searchParam={searchParam}
        // center={centerSearch.list}
        items={items.list}
      />
      <FileDownloadData fileName="商品マスタ" downloadData={downloadGridRows} downloadMode={downloadMode} columnDefs={downloadColDef} />
      <SearchBox 
        gridRef={search.searchBoxProps.gridRef}
        findSearchValue={search.searchBoxProps.findSearchValue}
        setFindSearchValue={search.searchBoxProps.setFindSearchValue}
        activeMatchNum={search.searchBoxProps.activeMatchNum}
        onFindChanged={search.searchBoxProps.onFindChanged}
        isVisible={search.searchBoxProps.isVisible}
        onHide={search.searchBoxProps.onHide}
      />
      <DataList
          list={items}
          onReload={onReload}
          supplier={supplierSearch.list}
          systype={typeCode}
          isLoading={isLoading}
          searchParam={searchParam}
          onUploadData={uploadData}
          updateTrigger={updateTrigger}
          setHasGridEdited={setHasGridEdited}
          setResultMessage={setGridMessage}
          setDownloadColumnDefs={setDownloadColDef}
          dialogEdits={alertDialogEdits}
          focusTarget={alertDialogFocusTarget}
          onFocusHandled={() => setAlertDialogFocusTarget(undefined)}
          findSearchValue={search.gridSearchProps.findSearchValue}
          onFindChanged={search.gridSearchProps.onFindChanged}
          gridRef={search.gridRef}
          isSearchVisible={search.searchBoxProps.isVisible}
        />
    </Box>
          
  )
}

export default MasterItem;
